import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Text,
  Button,
  Group,
  Menu,
  TextInput,
  ActionIcon,
  SegmentedControl,
} from '@mantine/core';
import {
  IconRefresh,
  IconEye,
  IconMaximize,
  IconPrinter,
  IconSearch,
  IconX,
  IconChevronDown,
  IconFilterCheck,
  IconDeselect,
} from '@tabler/icons-react';
import type { ReportMeta } from '@ayocore/platform/reporting/common/types';
import { listReports, generateReport } from '@ayocore/platform/reporting/client/rpc';
import { ColumnDef, Row, SortingState } from '@tanstack/react-table';
import cn from 'classnames';
import RepositoryTableWrapper, { RepositoryTableWrapperHandle } from './RepositoryTableWrapper';
import { FullScreen } from '@ayocore/platform/client/components/FullScreen';
import { AtomicLoader } from '@ayocore/platform/client/components/elements';
import logger from '@ayocore/platform/common/helpers/logger';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
// типы меты берём из контроллера
import { generateColumnFromSchema } from './columns/generators';
import { RepositoryController } from './RepositoryController';
import { useRepositorySelector } from './useRepositoryController';
import { printRecord } from './actions/printRecord';
import RepositoryTableColumnSettingsModal from './RepositoryTableColumnSettingsModal';
import {
  useReactTableController,
  useReactTableControllerInstance,
} from '@ayocore/platform/client/components/RTable/hooks/useReactTableController';
import { useDevice } from '@ayocore/platform/client/hooks/useDevice';
import { AdaptiveFilters } from './AdaptiveFilters';
import type { CustomView, RepositoryTableGroupBy, RepositoryTableSubRows } from './types';
import GroupedTableWrapper from './GroupedTableWrapper';
import { createRowKeyResolver } from './rowKey';
import type { AggregateItem, GroupByConfig } from '@ayocore/platform/common/types/aggregate';
import classes from './RepositoryTable.module.css';

/* -----------------------------------------------------------
 *  Типы действий и пропсов таблицы
 * ----------------------------------------------------------- */

/**
 * Пропсы для компонентов фильтров
 */
export interface FilterProps {
  // eslint-disable-next-line
  controller: RepositoryController<any>;
  disabled?: boolean;
}

/**
 * Тип компонента фильтра
 */
export type FilterComponent = React.ComponentType<FilterProps>;
/**
 * Аргумент handler'а bulk-действия — описание выделения.
 *
 * - `ids` — все выделенные ID-ключи (бизнес-id из `row.id`), независимо
 *   от текущей страницы. Универсальный кейс: выгрузки, массовые backend-операции.
 * - `rows` — объекты строк, ТОЛЬКО с текущей страницы (резолвятся из
 *   загруженных данных). Если запись выделена, но её страница не загружена,
 *   её здесь не будет. Используйте только когда действительно нужна
 *   развёрнутая строка (например, чтение нескольких полей сразу).
 *
 * @template TRow Тип сущности строки.
 */
export interface BulkActionSelection<TRow = unknown> {
  ids: string[];
  rows: TRow[];
}

/**
 * Императивный доступ к выделению таблицы снаружи.
 *
 * Заполняется в `selectionRef` на mount. Нужен для деструктивных массовых
 * действий (удаление): после операции часть выбранных строк исчезает из данных,
 * но их ключи остаются в выделении — счётчик «Выделено: N» зависает на старых ID.
 * Авто-сброс на refresh делать нельзя — он ломает межстраничный выбор.
 */
export interface RepositoryTableSelection {
  /** Полный сброс выделения по всем страницам (оба стора выбора). */
  clear: () => void;
}

/**
 * Массовое действие над выбранными строками таблицы.
 *
 * @template TRow Тип сущности строки.
 */
interface BulkAction<TRow = unknown> {
  title: string;
  handler: (selection: BulkActionSelection<TRow>) => void;
  color?: string;
  icon?: React.ReactNode;
}

/**
 * Базовые метаданные репозитория, необходимые для генерации колонок.
 */
// meta теперь берём из контроллера; локальная декларация не используется

/**
 * Пропсы таблицы-репозитория. Принимает RPC-клиент репозитория и выводит тип сущности строки автоматически.
 * @template T Тип сущности строк (например, Partner)
 */
export interface RepositoryTableProps<T extends Record<string, unknown>> {
  id: string;
  repository: RpcRepositoryClient<T>;
  className?: string;
  rowHeight?: number;
  // Fixed height for scrolling only rows
  tableBodyHeight?: number | string;
  // Page size for server-side pagination (default 25)
  defaultPageSize?: number;
  // Optional column overrides
  columns?: ColumnDef<T>[];
  /** @deprecated Используйте defineColumns() в репозитории. Работает только без columnsConfig. */
  excludeFields?: string[];
  // Bulk actions
  bulkActions?: BulkAction<T>[];
  // Optional row props provider (e.g., dynamic row styles)
  getRowProps?: (row: Row<T>) => React.HTMLAttributes<HTMLDivElement>;
  onRowClick?: (row: Row<T>, rowData: T) => void;
  onRowDoubleClick?: (row: Row<T>, rowData: T) => void;
  // Custom print handler
  onPrint?: (activeRow: T) => void;
  // Custom action buttons for toolbar.
  // Можно передать массив ReactNode или функцию, которая получает controller и возвращает массив.
  customActions?: React.ReactNode[] | ((controller: RepositoryController<T>) => React.ReactNode[]);
  /**
   * Дополнительные кнопки правого кластера тулбара (рядом с «Колонки/Полный экран/Печать/Обновить»).
   * Отрисовываются как первые элементы кластера. Используется для иконочных переходов
   * (например, открыть отчёт по этому списку).
   */
  extraToolbarActions?: React.ReactNode[];
  // Filter components
  filters?: FilterComponent[];
  // Refresh trigger - increment to refresh table data
  refreshTrigger?: number;
  /** Показывать поле поиска (default: true) */
  showSearch?: boolean;
  /** Кастомные представления списка (карточки, компактный список и т.п.) */
  customViews?: CustomView<T>[];
  /** Начальное представление: "table" или id из customViews */
  defaultView?: 'table' | string;
  /** Показывать переключатель "Таблица / ...". Не влияет на доступность самих вьюх. */
  showViewSwitcher?: boolean;
  /** Название встроенного табличного вида в переключателе (default: "Таблица"). */
  tableViewLabel?: string;
  /** Иконка встроенного табличного вида в переключателе (перед названием). */
  tableViewIcon?: React.ReactNode;
  /**
   * Показывать тогглер «Только выделенные» (default: true). Режим фильтрует
   * сервером по whereIn('id', выбор); несовместим с составным getRowId
   * (напр. `file-N`/`folder-N` на Диске) — там отключать. См. DRIVE_TODO.
   */
  showOnlySelectedToggle?: boolean;
  /** Показывать тулбар (поиск, фильтры, системные кнопки). */
  showToolbar?: boolean;
  /** Показывать нижний блок: пагинацию и счётчик «Строк: n» / агрегаты (default: true). */
  showBottom?: boolean;
  /**
   * Вертикальные разделители колонок (default: true).
   * false — «списочный» вид: только горизонтальные линии строк.
   * 'header' — строки остаются «списком», в шапке укороченные разделители
   * по центру высоты (как в Finder).
   */
  withColumnBorders?: boolean | 'header';
  /**
   * Какие строки можно выбирать чекбоксом (при включённых bulkActions).
   * По умолчанию выбираемы все. Пример: смешанные списки, где bulk-операции
   * применимы только к части записей.
   */
  isRowSelectable?: (row: Row<T>) => boolean;
  /**
   * Кастомный ключ строки для selection/rendering. По умолчанию row.id —
   * переопределяйте для смешанных списков, где id разных сущностей могут
   * совпадать (ключ обязан быть уникален в пределах выборки).
   */
  getRowId?: (row: T, index: number) => string;
  /** Поле для сортировки по умолчанию (если в схеме нет created_at, передать 'id' или 'ins_date'). */
  defaultSortField?: string;
  /** Направление сортировки по умолчанию. По умолчанию 'desc'. */
  defaultSortOrder?: 'asc' | 'desc';
  // Mobile card renderer - если передан и устройство мобильное, используется вместо таблицы
  mobileCard?: (row: T) => React.ReactNode;
  /**
   * Конфигурация серверных агрегатов (sum, avg, min, max, count).
   * Результаты отображаются в зоне пагинации слева.
   * @example [{ fn: 'sum', field: 'total_amount', label: 'Сумма заказов' }]
   */
  aggregates?: AggregateItem<T>[];
  /**
   * Режим группировки: строки таблицы — группы по значению колонки, считает их
   * сервер по всей выборке (с учётом фильтров и поиска), пагинация — по группам.
   *
   * Раскрытие группы догружает её строки (ленивая подгрузка, до 200 строк) и
   * показывает их дочерними строками той же таблицы — колонки и ширины у итога
   * и у строк совпадают. Агрегат группы выводится в колонке своего поля.
   *
   * Чекбоксы доступны только у дочерних строк (у итога нет бизнес-id), поэтому
   * bulkActions работают по выбранным строкам раскрытых групп.
   *
   * Ключ группировки — только реальная скалярная колонка схемы: по виртуальной
   * (withColumn), `timestamp`, `jsonb`, массиву или геометрии сервер откажет
   * понятной ошибкой (см. `GROUP_BY_FIELD_TYPES`).
   *
   * Работают как в обычном режиме, но применяются к ДОЧЕРНИМ строкам (строка-итог
   * не сущность): `getRowId`, `getRowProps`, `onRowClick`, `onRowDoubleClick`,
   * `isRowSelectable`. Клик по строке-итогу раскрывает группу, у неё свой фон.
   *
   * Недоступны: `customViews` (переключатель вьюх скрыт), `mobileCard`,
   * `renderSubComponent` (экспандер занят деревом) и сортировка кликом по
   * заголовку — порядок групп задаётся `groupBy.orderBy`.
   *
   * @example groupBy={{ field: 'executor', aggregates: [{ fn: 'sum', field: 'amount' }] }}
   */
  groupBy?: RepositoryTableGroupBy<T>;
  /**
   * Если true и передан columns — показывает только колонки из columns, игнорируя auto-генерацию из схемы.
   * Удобно для встроенных пикеров и таблиц с фиксированным набором колонок.
   */
  strictColumns?: boolean;
  /**
   * Контент, показываемый когда список пуст (нет результатов по фильтрам/поиску).
   * По умолчанию: "Ничего не найдено".
   */
  emptyPlaceholder?: React.ReactNode;
  /** Подсказка (placeholder) для поля поиска. По умолчанию: "Поиск... (Нажмите Enter)" */
  searchPlaceholder?: string;
  /**
   * Минимальная длина поискового запроса, при которой поиск вообще запускается.
   * Запросы короче (например 1–2 символа) игнорируются: кнопка поиска не активна,
   * Enter не запускает поиск. Пустая строка всегда сбрасывает поиск.
   * По умолчанию: 1 (без ограничений).
   */
  minSearchLength?: number;
  /**
   * Рендер раскрытого содержимого строки.
   * Если передан — автоматически добавляется колонка-expander (плюсик).
   */
  renderSubComponent?: (props: { row: Row<T> }) => React.ReactElement;
  /**
   * Дерево раскрываемых строк: у части строк есть дочерние, которые
   * догружаются при раскрытии и рисуются теми же колонками (см.
   * `RepositoryTableSubRows`). Работает в табличном виде без `groupBy`;
   * несовместим с `renderSubComponent` — оба хотят экспандер строки.
   */
  subRows?: RepositoryTableSubRows<T>;
  /**
   * Предикат: может ли строка раскрываться. По умолчанию: все строки.
   * Работает только вместе с renderSubComponent.
   */
  getRowCanExpand?: (row: Row<T>) => boolean;
  /**
   * Mutable ref для доступа к RepositoryController снаружи. Заполняется один
   * раз на mount. Нужен в редких сценариях — например, пикер с категорийным
   * деревом, который при выборе новой категории должен явно `setPage(1)`
   * (т.к. setRepository пагинацию не сбрасывает).
   */
  controllerRef?: React.MutableRefObject<RepositoryController<T> | null>;
  /**
   * Mutable ref для императивного сброса выделения снаружи. Заполняется на mount.
   * Нужен после деструктивных массовых действий (удаление): часть выбранных строк
   * исчезает, и выделение надо явно сбросить, иначе «Выделено: N» зависнет на
   * старых ID. Авто-сброс на refresh недопустим — ломает межстраничный выбор.
   */
  selectionRef?: React.MutableRefObject<RepositoryTableSelection | null>;
  /**
   * Начальный выбор строк (бизнес-id) для row-selection (bulkActions). В отличие от
   * восстановления из localStorage — ЯВНО задаёт выбор при монтировании, перекрывая
   * сохранённый (в т.ч. пустым массивом — сбрасывает). Для пикеров, где набор приходит
   * извне (редактирование): открыли с одними — отмечены они, а не остаток прошлого
   * открытия. Сидит оба стора выбора (persist-контроллер + tanstack-галочки).
   */
  initialSelectedIds?: string[];
}

/**
 * Компонент таблицы данных, работающий с RPC-репозиториями. Тип строки выводится из пропса `repository`.
 */
export default function RepositoryTable<T extends Record<string, unknown>>({
  id,
  repository,
  className = '',
  rowHeight = 1.5,
  tableBodyHeight,
  defaultPageSize = 25,
  columns: customColumns,
  bulkActions,
  getRowProps,
  onRowClick,
  onRowDoubleClick,
  onPrint,
  customActions,
  extraToolbarActions,
  filters,
  refreshTrigger,
  showSearch = true,
  customViews,
  defaultView = 'table',
  showViewSwitcher = true,
  tableViewLabel = 'Таблица',
  tableViewIcon,
  showOnlySelectedToggle = true,
  showToolbar = true,
  showBottom = true,
  withColumnBorders = true,
  isRowSelectable,
  getRowId,
  defaultSortField,
  defaultSortOrder,
  mobileCard,
  aggregates,
  groupBy,
  strictColumns,
  emptyPlaceholder = 'Ничего не найдено',
  searchPlaceholder = 'Поиск... Enter',
  minSearchLength = 1,
  renderSubComponent,
  getRowCanExpand,
  subRows,
  controllerRef,
  selectionRef,
  initialSelectedIds,
}: RepositoryTableProps<T>) {
  type TRow = T;

  const [columnsModalOpen, setColumnsModalOpen] = useState<boolean>(false);

  // Получаем контроллер RTable - единственный источник правды для настроек колонок
  const rtableController = useReactTableControllerInstance(id);

  // Подписываемся на состояние контроллера для реактивности
  const columnVisibility = useReactTableController(id, s => s.columnVisibility);
  const columnOrder = useReactTableController(id, s => s.columnOrder);
  const rowSelection = useReactTableController(id, s => s.rowSelection);

  // Контроллер создаётся один раз из входного репозитория (мемоизирован)
  const controller = useMemo(
    () =>
      new RepositoryController<TRow>(
        repository,
        id,
        defaultPageSize || 25,
        defaultSortField ?? 'created_at',
        defaultSortOrder ?? 'desc'
      ),
    []
  );

  // Прокидываем контроллер во внешний ref если есть — для редких сценариев
  // (например, picker, который явно дёргает setPage при смене контекста).
  useEffect(() => {
    if (controllerRef) controllerRef.current = controller;
    return () => {
      if (controllerRef) controllerRef.current = null;
    };
  }, [controllerRef, controller]);

  // Синхронизация внешних изменений ссылки repository с контроллером:
  // позволяет менять базовую цепочку (например, filterByCategory) без пересоздания
  // всего компонента — активные фильтры/поиск/сортировка сохраняются.
  const firstRepoSyncRef = useRef(true);
  useEffect(() => {
    if (firstRepoSyncRef.current) {
      firstRepoSyncRef.current = false;
      return;
    }
    controller.setRepository(repository);
  }, [controller, repository]);

  // Плоский репозиторий дерева — источник выборок по выделенным (см. subRows).
  const flatRepository = subRows?.flatRepository;
  useEffect(() => {
    controller.setFlatRepository(flatRepository ?? null);
  }, [controller, flatRepository]);
  // Резолв customActions: поддерживаем и ReactNode[] и функцию с controller
  const resolvedCustomActions = useMemo(
    () => (typeof customActions === 'function' ? customActions(controller) : customActions),
    [customActions, controller]
  );

  // Передаём конфиг агрегатов в контроллер
  useEffect(() => {
    controller.setAggregateConfig(aggregates ?? null);
  }, [controller, aggregates]);

  // Зависимость эффекта ниже — по ЗНАЧЕНИЮ, а не по ссылке: проп почти всегда
  // объект-литерал в JSX, и на identity эффект дёргал бы refresh() на каждый
  // рендер родителя.
  //
  // В конфиг (и значит в ключ) попадает только то, что влияет на SQL: у агрегата
  // это fn и field. Подписи (`label` элемента, `label`/`renderValue` пропа)
  // меняют отрисовку — сервер о них не знает, и перезапрос из-за них был бы
  // лишним.
  const groupQuery = useMemo(() => {
    if (!groupBy) return { key: null, config: null };
    const config: GroupByConfig<TRow> = {
      field: groupBy.field,
      aggregates: groupBy.aggregates?.map(({ fn, field }) => ({ fn, field })),
      orderBy: groupBy.orderBy,
    };
    return { key: JSON.stringify(config), config };
  }, [groupBy]);

  // Конфиг группировки читается при следующей загрузке, поэтому после смены
  // режима таблицу надо перезапросить: иначе на экране остались бы строки
  // сущностей, хотя таблица уже в режиме групп (и наоборот).
  // Зависимость — ключ (значение), а не объект конфига: иначе эффект срабатывал
  // бы на каждый рендер родителя и дёргал лишний запрос.
  useEffect(() => {
    controller.setGroupConfig(groupQuery.config);
    controller.refresh();
  }, [controller, groupQuery.key]);

  // Подписка на состояние контроллера через селекторы (избегаем лишних ререндеров)
  // Данные таблицы не читаем в этом уровне — подписка в Wrapper
  const isLoading = useRepositorySelector(controller, s => s.isLoading);
  const error = useRepositorySelector(controller, s => s.error);
  const meta = useRepositorySelector(controller, s => s.meta);
  const totalCount = useRepositorySelector(controller, s => s.totalCount);
  const aggregateData = useRepositorySelector(controller, s => s.aggregates);
  const aggregatesLoading = useRepositorySelector(controller, s => s.aggregatesLoading);
  const onlySelectedMode = useRepositorySelector(controller, s => s.onlySelectedMode);
  const page = useRepositorySelector(controller, () => controller.getQuery().page);
  const pageSize = useRepositorySelector(controller, () => controller.getQuery().pageSize);
  const refreshing = isLoading;

  // Отчёты, доступные для этого репозитория (режим 'single')
  const [availableReports, setAvailableReports] = useState<ReportMeta[]>([]);
  const [printLoading, setPrintLoading] = useState(false);

  useEffect(() => {
    const code = repository.getRepositoryCode();
    void listReports(code as Parameters<typeof listReports>[0], 'single')
      .then(setAvailableReports)
      .catch(() => {});
  }, []);

  const resolveDefaultView = useCallback((): 'table' | string => {
    if (defaultView === 'table') {
      return 'table';
    }
    if (customViews?.some(view => view.id === defaultView)) {
      return defaultView;
    }
    return 'table';
  }, [defaultView, customViews]);

  // Выбранный вид (Список/Плитка/…) персистится по tableId в ReactTableController
  // (aya-table-configs) — как настройки колонок: переживает F5. Невалидный
  // сохранённый вид (вьюха пропала из customViews) — откат к defaultView.
  const persistedView = useReactTableController(id, s => s.activeView);
  const activeView = useMemo<'table' | string>(() => {
    if (persistedView === 'table') return 'table';
    if (persistedView && customViews?.some(view => view.id === persistedView)) return persistedView;
    return resolveDefaultView();
  }, [persistedView, customViews, resolveDefaultView]);
  const setActiveView = useCallback(
    (view: string) => rtableController.setActiveView(view),
    [rtableController]
  );

  // Device detection for mobile view
  const { isMobile } = useDevice();

  // Get rows data from controller for mobile view
  const rows = useRepositorySelector(controller, s => s.data) as T[];

  // Защита от ложного empty state до первой загрузки
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    if (!isLoading) setHasLoaded(true);
  }, [isLoading]);

  // Стабилизация метаданных
  const stableMeta = useMemo(() => meta, [meta]);

  // Декларативные критерии берём из контроллера через селекторы
  const sorting = useRepositorySelector(controller, () => controller.getQuery().sorting);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeRow, setActiveRow] = useState<TRow | null>(null); // Track active/focused row

  // Состояние поиска, инициализированное из контроллера (восстанавливается из URL)
  const [searchValue, setSearchValue] = useState(() => {
    return controller.getQuery().search;
  });

  // Фильтров нет — удалено

  // Храним последнюю сортировку в ref для сравнения без пересоздания колбэка
  const sortingRef = useRef<SortingState>(sorting);
  useEffect(() => {
    sortingRef.current = sorting;
  }, [sorting]);

  // Handle sorting changes: если страница не 1 — просто сбросим на 1 (загрузит эффект по странице),
  // иначе сразу обновим данные
  const handleSortingChange = useCallback(
    async (next?: SortingState) => {
      await Promise.resolve();
      if (!next || next.length === 0) return;
      if (JSON.stringify(next) === JSON.stringify(sortingRef.current)) return;
      controller.setSorting(next);
    },
    [controller]
  );

  // Сброс selection на refreshTrigger не нужен: ключ rowSelection — бизнес-id,
  // поэтому галочки удалённых записей автоматически перестают рендериться
  // (ID отсутствует в data), а обновлённые — сохраняют выбор по тому же ID.

  // Источник правды по выбору — RepositoryController (переживает рефреш через localStorage).
  const selectedIds = useRepositorySelector(controller, s => s.selectedIds);

  // Сидинг таблицы восстановленным из localStorage выбором (один раз на маунте):
  // после F5 синглтон ReactTableController пустой, а контроллер уже восстановил выбор.
  const selectionSeededRef = useRef(false);
  useEffect(() => {
    if (selectionSeededRef.current) return;
    selectionSeededRef.current = true;
    if (initialSelectedIds !== undefined) {
      // Явный стартовый выбор (пикер): перекрываем localStorage, сидим ОБА стора —
      // persist-контроллер и tanstack-галочки. Пустой массив осознанно сбрасывает.
      const seed = Object.fromEntries(initialSelectedIds.map(v => [String(v), true]));
      controller.setRowSelection(seed);
      rtableController.setRowSelection(seed);
      return;
    }
    const restored = controller.getRowSelection();
    if (Object.keys(restored).length > 0) {
      rtableController.setRowSelection(restored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, rtableController]);

  /**
   * Рантайм-синхронизация «контроллер → галочки TanStack». Выбор меняют не
   * только клики по таблице (тот путь уже пишет в контроллер через
   * onRowSelectionChange), но и кастомные вьюхи (плитки Диска) — напрямую в
   * контроллере (источник правды). Без обратного канала таблица узнаёт о таком
   * выборе только на маунте (сидинг выше) — рассинхрон до F5.
   * Прямой setRowSelection стора НЕ дёргает onRowSelectionChange (он вызывается
   * лишь из TanStack-апдейтера по клику), поэтому эха нет; сравнение наборов —
   * controller.isSelectionEqual (юнит-покрыто) — отсекает холостые обновления.
   */
  useEffect(() => {
    if (controller.isSelectionEqual(rowSelection)) return;
    rtableController.setRowSelection(controller.getRowSelection());
  }, [selectedIds, rowSelection, controller, rtableController]);

  /**
   * В режиме «только выделенные» синхронизируем whereIn-фильтр с актуальным списком ID:
   * — юзер добавил галочку → список растёт → подгружаем;
   * — юзер снял последнюю → автоматически выходим из режима.
   */
  useEffect(() => {
    if (!onlySelectedMode) return;
    if (selectedIds.length === 0) {
      controller.setOnlySelected(null);
      return;
    }
    controller.setOnlySelected(selectedIds);
  }, [onlySelectedMode, selectedIds, controller]);

  /** Тогглер режима «только выделенные». */
  const handleToggleOnlySelected = useCallback(() => {
    if (onlySelectedMode) {
      controller.setOnlySelected(null);
    } else {
      if (selectedIds.length === 0) return;
      controller.setOnlySelected(selectedIds);
    }
  }, [onlySelectedMode, selectedIds, controller]);

  /**
   * Полный сброс выбора по всем страницам. Чистим оба стора:
   * контроллер (бизнес-выбор + localStorage) и ReactTableController (галочки в TanStack).
   * Режим «только выделенные» при пустом выборе выключается эффектом синхронизации.
   */
  const handleClearSelection = useCallback(() => {
    controller.setRowSelection({});
    rtableController.setRowSelection({});
  }, [controller, rtableController]);

  // Прокидываем императивный сброс выделения во внешний ref (если есть).
  // Используется после деструктивных массовых действий — см. selectionRef в пропсах.
  useEffect(() => {
    if (!selectionRef) return;
    selectionRef.current = { clear: handleClearSelection };
    return () => {
      if (selectionRef) selectionRef.current = null;
    };
  }, [selectionRef, handleClearSelection]);

  // Calculate selected rows from rowSelection state
  const wrapperRef = useRef<RepositoryTableWrapperHandle<TRow>>(null);

  // Default delete action перенесём в обёртку при необходимости

  // Bulk actions вычисляются и используются в меню, rows резолвятся через wrapper

  // Auto-generate columns from schema (мемоизированы)
  const autoColumns = useMemo(() => {
    if (!stableMeta?.schema?.properties) return [];

    const columns: ColumnDef<TRow>[] = [];
    const props = stableMeta.schema.properties as Record<string, unknown>;

    // ID — всегда первая колонка
    if (props.id) {
      columns.push({
        id: 'id',
        header: 'ID',
        accessorKey: 'id',
        enableSorting: true,
        size: 80,
      });
    }

    // Колонки из серверного конфига (columnsConfig всегда присутствует)
    // id и created_at добавляются отдельно (выше/ниже), исключаем чтобы не дублировать
    const fieldsToGenerate = stableMeta.columnsConfig.columns
      .map(c => c.field)
      .filter(f => f !== 'id' && f !== 'created_at');

    for (const fieldName of fieldsToGenerate) {
      const fieldSchema = props[fieldName];
      if (!fieldSchema) continue;

      const column = generateColumnFromSchema<TRow>(fieldName, fieldSchema as never);
      if (column) {
        columns.push(column);
      }
    }

    // created_at — всегда последняя (системное поле)
    if (props.created_at) {
      columns.push({
        id: 'created_at',
        header: 'Создан',
        accessorKey: 'created_at',
        enableSorting: true,
        size: 120,
        cell: ({ row }: { row: Row<TRow> }) => (
          <Text size='xs' c='dimmed'>
            {new Date(
              (row.original as Record<string, unknown>).created_at as unknown as string
            ).toLocaleDateString()}
          </Text>
        ),
      });
    }

    return columns;
  }, [stableMeta]);

  /**
   * Генерирует конфигурацию колонки на основе схемы поля.
   */
  // генераторы колонок вынесены в ./columns/generators

  // Инициализация порядка и видимости из серверного конфига
  // Применяем один раз при первом получении meta, если нет сохранённых настроек в localStorage
  const columnsConfigAppliedRef = useRef(false);
  useEffect(() => {
    if (!stableMeta || !rtableController) return;
    if (columnsConfigAppliedRef.current) return;
    if (rtableController.hasStoredSettings()) {
      columnsConfigAppliedRef.current = true;
      return;
    }

    const { columns } = stableMeta.columnsConfig;

    if (customColumns?.length) {
      // customColumns задают порядок из кода — приоритет над defineColumns
      const visibleIds = customColumns.map(c => c.id).filter((id): id is string => !!id);
      const visibleSet = new Set(visibleIds);
      const schemaHidden = columns.map(c => c.field).filter(f => !visibleSet.has(f));
      const defineHidden = columns
        .filter(c => c.hidden)
        .map(c => c.field)
        .filter(f => !visibleSet.has(f));
      const hiddenIds = [...new Set([...defineHidden, ...schemaHidden])];

      rtableController.setColumnOrder(visibleIds);
      if (hiddenIds.length > 0) {
        rtableController.setHiddenColumns(hiddenIds);
      }
    } else {
      // Только defineColumns — серверная конфигурация
      const hiddenIds = columns.filter(c => c.hidden).map(c => c.field);
      const order = ['id', ...columns.map(c => c.field), 'created_at'];

      rtableController.setColumnOrder(order);
      if (hiddenIds.length > 0) {
        rtableController.setHiddenColumns(hiddenIds);
      }
    }
    columnsConfigAppliedRef.current = true;
  }, [stableMeta, rtableController]);

  // Получаем видимые колонки через контроллер (мерджит auto + custom и применяет видимость)
  const visibleColumns = useMemo(
    () => rtableController.getVisibleColumns(autoColumns, customColumns, strictColumns),
    [rtableController, autoColumns, customColumns, columnVisibility, strictColumns]
  );

  // Данные для модалки настроек колонок (отсортированные видимые/скрытые)
  const { visible: modalVisibleColumns, hidden: modalHiddenColumns } = useMemo(
    () =>
      rtableController.getModalColumns(
        autoColumns.map(col => ({ id: col.id as string, header: col.header as React.ReactNode })),
        customColumns?.map(col => ({ id: col.id as string, header: col.header as React.ReactNode })),
        strictColumns
      ),
    [rtableController, autoColumns, customColumns, columnOrder, columnVisibility, strictColumns]
  );

  // Применение параметров и загрузка — внутри контроллера
  // totalCount теперь загружается контроллером через count()
  // Загрузка происходит через методы контроллера (setPage/setSorting/setSearch/refresh)
  // URL синхронизируется самим контроллером

  // Фильтров нет — пропускаем

  // Refresh function for the refresh button
  const handleRefresh = useCallback(async () => {
    await controller.refresh();
  }, [controller]);

  // Search handling functions - Swiss watch approach!
  const handleSearchClear = (event: React.MouseEvent) => {
    event.preventDefault();
    setSearchValue('');
    controller.setSearch('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setSearchValue(value);
    if (value === '') {
      controller.setSearch('');
    }
  };

  const handleSearchSubmit = () => {
    const term = searchValue.trim();
    // Слишком короткий запрос (1..minSearchLength-1 символов) — не ищем.
    // Пустая строка проходит дальше и сбрасывает активный поиск.
    if (term.length > 0 && term.length < minSearchLength) return;
    controller.setSearch(searchValue);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Синхронизация состояния поиска с контроллером при монтировании (восстановление из URL)
  // Используем useRef чтобы выполнить только один раз
  const searchInitializedRef = useRef(false);
  useEffect(() => {
    if (!searchInitializedRef.current) {
      const query = controller.getQuery();
      if (query.search && query.search !== searchValue) {
        setSearchValue(query.search);
      }
      searchInitializedRef.current = true;
    }
  }, [controller]);

  // Ключ строки — один резолвер на все представления (галочки, персист выбора,
  // разрешение выбранных строк в bulkActions).
  const resolveRowKey = useMemo(() => createRowKeyResolver<TRow>(getRowId), [getRowId]);

  // Handle row click to set active row
  const handleRowClick = useCallback((row: Row<TRow>, rowData: TRow) => {
    setActiveRow(rowData);
  }, []);

  // Combined row click handler - calls both internal and external handlers
  const combinedRowClick = useCallback(
    (row: Row<TRow>, rowData: TRow) => {
      // Always call internal handler to set activeRow
      handleRowClick(row, rowData);

      // Call external handler if provided
      if (onRowClick) {
        onRowClick(row, rowData);
      }
    },
    [handleRowClick, onRowClick]
  );

  // Handle print active row
  const handlePrint = useCallback(() => {
    if (!activeRow) {
      logger.warn('⚠️ [RepositoryTable] No active row to print');
      return;
    }

    // Use custom print handler if provided
    if (onPrint) {
      logger.info('🎯 [RepositoryTable] Using custom print handler');
      onPrint(activeRow);
      return;
    }
    // Default print behavior (вынесено в actions)
    logger.info('🖨️ [RepositoryTable] Using default print behavior');
    printRecord(meta?.tableName, activeRow as unknown as Record<string, unknown>);
    logger.info('🖨️ [RepositoryTable] Printing active row:', activeRow);
  }, [activeRow, meta, onPrint]);

  const handleGenerateReport = useCallback(
    async (slug: string) => {
      if (!activeRow) return;
      try {
        setPrintLoading(true);
        const id = (activeRow as Record<string, unknown>).id;
        const { url } = await generateReport(slug, { documentId: String(id) });
        window.open(url, '_blank');
      } catch (err) {
        logger.error('[RepositoryTable] Ошибка генерации отчёта:', err);
      } finally {
        setPrintLoading(false);
      }
    },
    [activeRow]
  );

  // Initial load on mount
  useEffect(() => {
    void controller.refresh();
  }, [controller]);

  // Refresh on external trigger
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      controller.refresh();
    }
  }, [refreshTrigger, controller]);

  // Не рендерить таблицу пока meta не загружен (общее для всех устройств)
  if (!meta) {
    return (
      <div className={classes.metaLoader}>
        <AtomicLoader size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <Text c='red'>❌ Error: {String(error)}</Text>
      </div>
    );
  }

  // Slots — готовые UI-элементы тулбара для custom views
  // Кнопка поиска активна только когда запрос достиг минимальной длины.
  const canSubmitSearch = searchValue.trim().length >= minSearchLength;
  const searchRightSection = (
    <Group gap={2} wrap='nowrap'>
      <ActionIcon
        size='xs'
        variant='subtle'
        onClick={canSubmitSearch ? handleSearchSubmit : undefined}
        aria-label='Найти'
        disabled={!canSubmitSearch}
        style={!canSubmitSearch ? { opacity: 0.4 } : undefined}
      >
        <IconSearch size={14} />
      </ActionIcon>
      {searchValue && (
        <ActionIcon
          size='xs'
          variant='subtle'
          onClick={handleSearchClear}
          aria-label='Очистить поиск'
        >
          <IconX size={14} />
        </ActionIcon>
      )}
    </Group>
  );

  const searchSlot = showSearch ? (
    <TextInput
      value={searchValue}
      onChange={handleSearchChange}
      placeholder={searchPlaceholder}
      onKeyDown={handleSearchKeyPress}
      rightSection={searchRightSection}
      rightSectionWidth={searchValue ? 48 : 24}
      className={classes.searchInput}
      size='xs'
    />
  ) : null;

  const filtersSlot =
    filters && filters.length > 0 ? (
      <AdaptiveFilters>
        {filters.map((FilterComponent, index) => (
          <FilterComponent key={index} controller={controller} />
        ))}
      </AdaptiveFilters>
    ) : null;

  // Данные переключателя видов: метка = иконка (если задана) + название.
  // Общие для обоих мест рендера (слот actionsSlot и тулбар).
  const viewLabel = (icon: React.ReactNode, label: string): React.ReactNode =>
    icon ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {icon}
        {label}
      </span>
    ) : (
      label
    );
  const viewSwitcherData = [
    { value: 'table', label: viewLabel(tableViewIcon, tableViewLabel) },
    ...(customViews?.map(v => ({ value: v.id, label: viewLabel(v.icon, v.title) })) ?? []),
  ];

  const viewSwitcherSlot =
    showViewSwitcher && customViews && customViews.length > 0 ? (
      <SegmentedControl
        size='xs'
        value={activeView}
        onChange={v => setActiveView(v)}
        data={viewSwitcherData}
      />
    ) : null;

  const actionsSlot =
    resolvedCustomActions && resolvedCustomActions.length > 0 ? (
      <>
        {resolvedCustomActions.map((action, index) => (
          <React.Fragment key={index}>{action}</React.Fragment>
        ))}
      </>
    ) : null;

  const refreshSlot = (
    <Button
      variant='outline'
      size='xs'
      onClick={handleRefresh}
      disabled={refreshing}
      className={classes.toolbarButton}
      aria-label='Обновить данные'
    >
      {refreshing ? <AtomicLoader size={14} /> : <IconRefresh size={14} />}
    </Button>
  );

  const customViewSlots = {
    search: searchSlot,
    filters: filtersSlot,
    viewSwitcher: viewSwitcherSlot,
    actions: actionsSlot,
    refresh: refreshSlot,
  };

  return (
    <div className={cn(classes.container, className)}>
      <FullScreen
        switch={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        title='Таблица - Полноэкранный режим'
      >
        {/* Toolbar */}
        {showToolbar && (
          <div className={classes.toolbar}>
            {/* Ряд 1: Поиск + Фильтры */}
            <Group gap='xs' wrap='wrap' className={classes.searchFiltersRow}>
              {/* Переключатель: Таблица / кастомные вьюхи */}
              {/* В режиме группировки вьюхи недоступны (ветка рендера ниже уходит
                  в групповое представление), поэтому переключатель скрыт — иначе
                  он обещал бы то, чего не делает. */}
              {!groupBy && showViewSwitcher && customViews && customViews.length > 0 && (
                <SegmentedControl
                  size='xs'
                  value={activeView}
                  onChange={v => setActiveView(v)}
                  data={viewSwitcherData}
                />
              )}

              {/* Search Input — доступен у всех RpcRepository */}
              {showSearch && (
                <TextInput
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  onKeyDown={handleSearchKeyPress}
                  rightSection={searchRightSection}
                  rightSectionWidth={searchValue ? 48 : 24}
                  className={classes.searchInput}
                  size='xs'
                />
              )}

              {/* Filter Components */}
              {filters && filters.length > 0 && (
                <AdaptiveFilters>
                  {filters.map((FilterComponent, index) => (
                    <FilterComponent key={index} controller={controller} />
                  ))}
                </AdaptiveFilters>
              )}
            </Group>

            {/* Ряд 2: Bulk + Custom (слева) | Тулбар (справа) */}
            {!isMobile && (
              <Group justify='space-between' align='center' gap='xs'>
                <Group gap='xs'>
                  {/* Bulk Actions Menu */}
                  {bulkActions && bulkActions.length > 0 && (
                    <Menu shadow='md' width={200} zIndex={10000}>
                      <Menu.Target>
                        {/* Активность — по selectedIds контроллера (источник правды
                            по выбору): работает и в табличном, и в custom-виде
                            (плитки), где локальный rowSelection не обновляется */}
                        <Button
                          variant={selectedIds.length > 0 ? 'filled' : 'outline'}
                          size='xs'
                          disabled={selectedIds.length === 0}
                          rightSection={<IconChevronDown size={12} />}
                          aria-label='Массовые действия'
                        >
                          Действия
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown className={classes.toolbarDropdown}>
                        {bulkActions.map((action, index) => (
                          <Menu.Item
                            key={index}
                            leftSection={action.icon}
                            color={action.color}
                            onClick={() => {
                              // В режиме группировки страница — это группы, а
                              // выбранные сущности лежат в раскрытых группах:
                              // строки отдаёт контроллер, ids те же.
                              const selectedRows = groupBy
                                ? controller.getSelectedGroupChildRows(resolveRowKey)
                                : activeView === 'table'
                                  ? (wrapperRef.current?.resolveSelectedRows(rowSelection) ?? [])
                                  : [];
                              action.handler({
                                ids: selectedIds,
                                rows: selectedRows as TRow[],
                              });
                            }}
                          >
                            {action.title}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}

                  {/* Сброс всего выбора (по всем страницам) — виден когда есть выделение */}
                  {bulkActions && bulkActions.length > 0 && selectedIds.length > 0 && (
                    <Button
                      variant='default'
                      size='xs'
                      leftSection={<IconDeselect size={14} />}
                      onClick={handleClearSelection}
                      aria-label='Снять выделение со всех строк'
                      title='Снять выделение со всех строк'
                    >
                      Снять выделение
                    </Button>
                  )}

                  {/* Тогглер «Только выделенные» — виден когда включён режим или есть выделение */}
                  {showOnlySelectedToggle &&
                    bulkActions &&
                    bulkActions.length > 0 &&
                    (selectedIds.length > 0 || onlySelectedMode) && (
                      <Button
                        variant={onlySelectedMode ? 'filled' : 'outline'}
                        color={onlySelectedMode ? 'violet' : undefined}
                        size='xs'
                        leftSection={<IconFilterCheck size={14} />}
                        onClick={handleToggleOnlySelected}
                        aria-label={
                          onlySelectedMode ? 'Показать все строки' : 'Показать только выделенные'
                        }
                        title={
                          onlySelectedMode
                            ? 'Показать все строки'
                            : 'Показать только выделенные строки'
                        }
                      >
                        {onlySelectedMode ? 'Все строки' : 'Только выделенные'}
                      </Button>
                    )}

                  {/* Custom Actions */}
                  {resolvedCustomActions?.map((action, index) => (
                    <React.Fragment key={index}>{action}</React.Fragment>
                  ))}
                </Group>

                <Group gap='xs' style={{ flexShrink: 0 }}>
                  {/* Дополнительные кнопки правого кластера от вызывающего компонента */}
                  {extraToolbarActions?.map((action, index) => (
                    <React.Fragment key={`extra-toolbar-${index}`}>{action}</React.Fragment>
                  ))}

                  {/* Columns Manager Trigger. Работает и в режиме группировки:
                      дочерние строки рисуются теми же колонками сущности, что и
                      обычный список, — модалка управляет именно ими. */}
                  <Button
                    variant='outline'
                    size='xs'
                    className={classes.toolbarButton}
                    aria-label='Настроить колонки'
                    onClick={() => setColumnsModalOpen(true)}
                  >
                    <IconEye size={14} />
                  </Button>

                  {/* Column Settings Modal */}
                  <RepositoryTableColumnSettingsModal
                    opened={columnsModalOpen}
                    onClose={() => setColumnsModalOpen(false)}
                    tableId={id}
                    visible={modalVisibleColumns}
                    hidden={modalHiddenColumns}
                    onApply={(nextOrder, nextHidden) => {
                      // Контроллер сам управляет персистентностью
                      rtableController.setColumnOrder(nextOrder);
                      rtableController.setHiddenColumns(nextHidden);
                      setColumnsModalOpen(false);
                    }}
                    onReset={() => {
                      if (customColumns?.length) {
                        // customColumns задают «правильный» порядок из кода — приоритет над defineColumns
                        const visibleIds = customColumns
                          .map(c => c.id)
                          .filter((id): id is string => !!id);
                        const visibleSet = new Set(visibleIds);

                        if (stableMeta) {
                          // Колонки из defineColumns, которых нет в customColumns — скрыты по умолчанию
                          const { columns } = stableMeta.columnsConfig;
                          // Колонки из схемы, которых нет в customColumns + defineColumns hidden (кроме тех что в customColumns)
                          const schemaHidden = columns
                            .map(c => c.field)
                            .filter(f => !visibleSet.has(f));
                          const defineHidden = columns
                            .filter(c => c.hidden)
                            .map(c => c.field)
                            .filter(f => !visibleSet.has(f));
                          const hiddenIds = [...new Set([...defineHidden, ...schemaHidden])];
                          columnsConfigAppliedRef.current = false;
                          return { visible: visibleIds, hidden: hiddenIds };
                        }
                        return { visible: visibleIds, hidden: [] };
                      }

                      if (stableMeta) {
                        // Только defineColumns — серверная конфигурация
                        const { columns } = stableMeta.columnsConfig;
                        const visibleIds = ['id', ...columns.map(c => c.field), 'created_at'];
                        const hiddenIds = columns.filter(c => c.hidden).map(c => c.field);
                        columnsConfigAppliedRef.current = false;
                        return { visible: visibleIds, hidden: hiddenIds };
                      }
                    }}
                  />

                  {/* Fullscreen Button */}
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => setIsFullScreen(true)}
                    className={classes.toolbarButton}
                    aria-label='Полноэкранный режим'
                  >
                    <IconMaximize size={14} />
                  </Button>

                  {/* Print Button: system reports or fallback */}
                  {availableReports.length > 0 ? (
                    <Menu shadow='sm' position='bottom-end'>
                      <Menu.Target>
                        <Button
                          variant='outline'
                          size='xs'
                          disabled={!activeRow}
                          loading={printLoading}
                          px='xs'
                          aria-label='Печать'
                          rightSection={<IconChevronDown size={12} />}
                        >
                          <IconPrinter size={14} />
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {availableReports.map(report => (
                          <Menu.Item
                            key={report.slug}
                            leftSection={<IconPrinter size={14} />}
                            disabled={!activeRow}
                            onClick={() => void handleGenerateReport(report.slug)}
                          >
                            {report.title}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  ) : (
                    <Button
                      variant='outline'
                      size='xs'
                      onClick={handlePrint}
                      disabled={!activeRow}
                      className={classes.toolbarButton}
                      aria-label='Печать активной строки'
                    >
                      <IconPrinter size={14} />
                    </Button>
                  )}

                  {/* Refresh Button */}
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className={classes.toolbarButton}
                    aria-label='Обновить данные'
                  >
                    {refreshing ? <AtomicLoader size={14} /> : <IconRefresh size={14} />}
                  </Button>
                </Group>
              </Group>
            )}
          </div>
        )}

        {/* Mobile view: карточки вместо таблицы. В режиме группировки карточек
            нет — mobileCard рисует сущность, а строк сущностей на экране нет. */}
        {isMobile && mobileCard && !groupBy ? (
          <div className={cn(classes.mobileList, className)}>
            {isLoading ? (
              <div className={classes.mobileLoader}>
                <AtomicLoader size={48} />
              </div>
            ) : rows.length === 0 && hasLoaded ? (
              <div className={classes.emptyPlaceholder}>
                <Text c='dimmed' size='sm'>
                  {emptyPlaceholder}
                </Text>
              </div>
            ) : (
              rows.map((row, idx) => {
                const rowId = row.id;
                // Используем комбинацию tableId + rowId для гарантии уникальности
                // Это предотвращает коллизии между разными таблицами и источниками данных
                // Используем префикс "row-id-" для строк с id и "row-idx-" для строк без id,
                // чтобы избежать коллизий когда id может содержать строки вида "row-5"
                const key =
                  rowId != null && (typeof rowId === 'string' || typeof rowId === 'number')
                    ? `${id}-row-id-${rowId}`
                    : `${id}-row-idx-${idx}`;
                return (
                  <div key={key} className={classes.mobileItem}>
                    {mobileCard(row)}
                  </div>
                );
              })
            )}
          </div>
        ) : groupBy ? (
          <GroupedTableWrapper<TRow>
            id={id}
            controller={controller}
            groupBy={groupBy}
            // Дочерние строки групп рисуются теми же колонками, что обычный режим
            columns={visibleColumns}
            // Чекбоксы у детей — только если у таблицы есть массовые действия
            // (как в обычном режиме: без bulkActions колонка выбора не нужна)
            enableChildSelection={!!bulkActions?.length}
            // Те же пропсы строки, что у обычного режима. Обёртка применяет их
            // только к дочерним строкам: строка-итог не сущность.
            getRowId={getRowId}
            getRowProps={getRowProps}
            onRowClick={combinedRowClick}
            onRowDoubleClick={onRowDoubleClick}
            isRowSelectable={isRowSelectable}
            className={classes.tableWrapper}
            rowHeight={rowHeight}
            bodyHeight={isFullScreen ? undefined : tableBodyHeight}
            withColumnBorders={withColumnBorders}
            showBottom={showBottom}
            emptyPlaceholder={hasLoaded && !isLoading ? emptyPlaceholder : undefined}
            aggregateData={aggregateData}
            aggregateConfig={aggregates}
            aggregatesLoading={aggregatesLoading}
          />
        ) : activeView === 'table' ? (
          <RepositoryTableWrapper
            ref={wrapperRef}
            id={id}
            controller={controller}
            showBottom={showBottom}
            withColumnBorders={withColumnBorders}
            columns={visibleColumns}
            className={classes.tableWrapper}
            getRowProps={getRowProps}
            rowHeight={rowHeight}
            // В полноэкранном режиме внешняя высота тела игнорируется: она
            // посчитана под встроенный экран (320–470 px) и оставила бы под
            // таблицей пустоту на весь монитор. Тело забирает остаток окна —
            // фильтры и тулбар сжимают его, а не выталкивают пагинацию вниз.
            bodyHeight={isFullScreen ? undefined : tableBodyHeight}
            enableRowSelection={bulkActions?.length ? (isRowSelectable ?? true) : false}
            getRowId={getRowId}
            onRowSelectionChange={updater => {
              const next =
                typeof updater === 'function' ? updater(controller.getRowSelection()) : updater;
              controller.setRowSelection(next);
            }}
            onRowClick={combinedRowClick}
            onRowDoubleClick={onRowDoubleClick}
            initialSorting={sorting}
            onSortingChange={handleSortingChange}
            onPaginationChange={p => controller.setPage(p?.current ?? 1)}
            aggregateData={aggregateData}
            aggregateConfig={aggregates}
            aggregatesLoading={aggregatesLoading}
            emptyPlaceholder={hasLoaded && !isLoading ? emptyPlaceholder : undefined}
            renderSubComponent={renderSubComponent}
            getRowCanExpand={getRowCanExpand}
            subRows={subRows}
          />
        ) : (
          (() => {
            const cv = customViews?.find(v => v.id === activeView);
            if (!cv) return null;
            return cv.render({
              rows,
              pagination: { current: page, total: totalCount, pageSize },
              isLoading,
              setPage: p => controller.setPage(p),
              controller,
              slots: customViewSlots,
            });
          })()
        )}
      </FullScreen>
    </div>
  );
}
